import { Commit } from "vuex";
import ApiService from "@/services/api.service";

type SettingsValue = unknown;

interface SettingsState {
    settings: Record<string, SettingsValue>;
    structure: Record<string, SettingsValue> | null;
}

const state: SettingsState = {
    settings: {},
    structure: null,
};

/**
 * Get a nested value from an object using dot notation path
 */
function getNestedValue(
    obj: Record<string, SettingsValue>,
    path: string,
): SettingsValue | undefined {
    const keys = path.split(".");
    let current: SettingsValue = obj;

    for (const key of keys) {
        if (
            current === null ||
            current === undefined ||
            typeof current !== "object"
        ) {
            return undefined;
        }
        current = (current as Record<string, SettingsValue>)[key];
    }

    return current;
}

const getters = {
    settings: (state: SettingsState) => state.settings,
    structure: (state: SettingsState) => state.structure,
    getSetting:
        (state: SettingsState) =>
        (path: string, defaultValue: SettingsValue = null) => {
            const value = getNestedValue(state.settings, path);
            return value !== undefined ? value : defaultValue;
        },
};

const mutations = {
    setSettings(state: SettingsState, settings: Record<string, SettingsValue>) {
        state.settings = settings;
    },
    setStructure(
        state: SettingsState,
        structure: Record<string, SettingsValue>,
    ) {
        state.structure = structure;
    },
    updateSetting(
        state: SettingsState,
        payload: { key: string; value: SettingsValue },
    ) {
        state.settings = {
            ...state.settings,
            [payload.key]: payload.value,
        };
    },
};

const actions = {
    async fetchSettings({ commit }: { commit: Commit }) {
        const response = await ApiService.get("/profile/settings");
        // Laravel JsonResponse wraps data, so response.data contains the nested structure
        const settingsData = response.data.data || response.data;
        commit("setSettings", settingsData);
        if (response.data.structure) {
            commit("setStructure", response.data.structure);
        }
        return settingsData;
    },
    async updateSettings(
        { commit }: { commit: Commit },
        settings: Record<string, SettingsValue>,
    ) {
        const response = await ApiService.put("/profile/settings", {
            settings,
        });
        // Laravel JsonResponse wraps data, so response.data contains the nested structure
        const settingsData = response.data.data || response.data;
        commit("setSettings", settingsData);
        return settingsData;
    },
    async updateSetting(
        { dispatch }: { dispatch: any },
        payload: { key: string; value: SettingsValue },
    ) {
        // Extract flat key for backend (backend stores flat keys like "reminders")
        const keys = payload.key.split(".");
        const flatKey = keys[keys.length - 1];

        // Update on backend with flat key
        await ApiService.put("/profile/settings", {
            settings: {
                [flatKey]: payload.value,
            },
        });

        // Refetch settings to get the updated nested structure
        await dispatch("fetchSettings");
    },
};

export const settings = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
